import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { getActionCallerBySocket } from "lib/utils/connection";

import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    getStcmMapListBaseUrl,
    postStcmMetadata,
} from "lib/consts/stcmMapList";
import { buildMapFilterYamlContent } from "lib/protocols/mapFilterYaml";
import { useMap } from "providers/navigation/MapProvider";
import { getMapProgressBaseUrl } from "lib/consts/mapProgress";
import {
    clearKeepoutFilterService,
    performClearMap,
    validateAndNormalizeMapBaseName,
    type ClearKeepoutFilterRequest,
} from "lib/protocols/mapLoad";
import { missionMapIdForStcmRelPath } from "lib/mission/missionStorageMaps";
import { performLoadMapAction, MapLoadCancelledError } from "lib/mission/performLoadMapAction";
import MapStorageProgressOverlay from "components/navigation/map/MapStorageProgressOverlay";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { authedFetch } from "lib/utils/authedFetch";
import { useDockedActionGate } from "lib/hooks/useDockedActionGate";
import { useTranslation } from "react-i18next";

export type MapLoadStatus = "idle" | "loading" | "success" | "error";

export type LoadStcmOptions = {
    /** mission_storage map UUID; skips stem lookup when set. */
    mapId?: string;
    /** Edited variant BMP (relative to map dir). Passed through to `/load_map`. */
    variantBmpRelativePath?: string;
    /** mission_storage map_files row for variant artifacts. */
    mapFileId?: string;
};

type MapManagerContextValue = {
    loadStcmFromRelativePath: (
        relativePath: string,
        options?: LoadStcmOptions,
    ) => Promise<void>;
    loadStatus: MapLoadStatus;
    loadError: string | null;
    clearLoadState: () => void;
    /** Relative `.stcm` last loaded on the robot (for keep-out reset on clear). */
    lastLoadedStcmRelPath: string | null;
    /** Save current map as `{name}.stcm` / `{name}.bmp`; optional metadata JSON via map list HTTP API. */
    saveMap: (baseName: string, remarks?: string) => Promise<void>;
    /** Clear map via perform_clear_map (device reset + reinit); shows progress overlay. */
    clearMap: () => void;
};

const MapManagerContext = createContext<MapManagerContextValue | null>(null);
MapManagerContext.displayName = "MapManager";

export const useMapManager = (): MapManagerContextValue => {
    const ctx = useContext(MapManagerContext);
    if (!ctx) {
        throw new Error("useMapManager must be used within MapManagerProvider");
    }
    return ctx;
};

const MapManagerProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation("control");
    const { controlSocket, callService, connectionState, selectedARC } =
        useARC();
    const { addToast } = useToast();
    const { mapMetadata, mapMetadataSeqRef } = useMap();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const { runWithDockedGateAsync, dockedGateModal } = useDockedActionGate();
    const actionCaller = useMemo(
        () => getActionCallerBySocket(controlSocket),
        [controlSocket],
    );

    const [loadStatus, setLoadStatus] = useState<MapLoadStatus>("idle");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [lastLoadedStcmRelPath, setLastLoadedStcmRelPath] = useState<string | null>(null);
    const [mapStorageProgress, setMapStorageProgress] = useState<{
        title: string;
        phase: string;
        progress: number;
        message: string;
        cancellable?: boolean;
    } | null>(null);
    const [cancellingLoadMap, setCancellingLoadMap] = useState(false);
    const activeLoadMapCancelRef = useRef<(() => void) | null>(null);

    const recordLoadedStcmPath = useCallback((stcmRelPath: string | null) => {
        const stcm = stcmRelPath?.replace(/\\/g, "/").trim() || null;
        lastLoadedStcmRelPathRef.current = stcm;
        setLastLoadedStcmRelPath(stcm);
    }, []);

    /** Last successfully loaded `.stcm` path (for keep-out reset on clear when /map_metadata is missing). */
    const lastLoadedStcmRelPathRef = useRef<string | null>(null);
    /** Latest MapProvider metadata (updated on every `/map_metadata` message). */
    const mapMetadataRef = useRef(mapMetadata);
    mapMetadataRef.current = mapMetadata;

    const cancelLoadMap = useCallback(() => {
        if (!activeLoadMapCancelRef.current) {
            return;
        }
        setCancellingLoadMap(true);
        try {
            activeLoadMapCancelRef.current();
        } catch (e) {
            setCancellingLoadMap(false);
            const msg = e instanceof Error ? e.message : "Failed to cancel map load";
            addToast({ type: "error", message: msg });
        }
    }, [addToast]);

    const runWithMapProgress = useCallback(
        async (
            title: string,
            host: string | null | undefined,
            op: () => Promise<void>,
            options?: {
                pollHttpProgress?: boolean;
                cancellable?: boolean;
                /** Force overlay even when map-progress HTTP is unavailable. */
                showOverlay?: boolean;
                initialPhase?: string;
                initialMessage?: string;
                /**
                 * Dismiss when HTTP progress returns to idle after a busy phase.
                 * Needed for long ROS services (e.g. perform_clear_map) whose
                 * Zenoh reply is dropped (~10s) while the robot keeps working.
                 */
                completeWhenProgressIdle?: boolean;
            },
        ) => {
            const base = getMapProgressBaseUrl(host ?? undefined, isSecure, arcId);
            const wantPoll = options?.pollHttpProgress ?? true;
            const pollHttp = wantPoll && Boolean(base);
            const showOverlay =
                options?.showOverlay ??
                (wantPoll ? Boolean(base) : true);
            const completeWhenIdle =
                options?.completeWhenProgressIdle ?? false;

            if (!showOverlay) {
                await op();
                return;
            }

            setMapStorageProgress({
                title,
                phase: options?.initialPhase ?? "idle",
                progress: 0,
                message: options?.initialMessage ?? "",
                cancellable: options?.cancellable ?? false,
            });

            let pollId: number | undefined;
            let sawBusyPhase = false;
            let resolveIdleAfterBusy: (() => void) | undefined;
            const idleAfterBusy = new Promise<void>((resolve) => {
                resolveIdleAfterBusy = resolve;
            });

            if (pollHttp && base) {
                const tick = async () => {
                    try {
                        const r = await authedFetch(`${base}/progress`, {
                            cache: "no-store",
                        });
                        if (!r.ok) return;
                        const j = (await r.json()) as {
                            phase?: string;
                            progress?: number;
                            message?: string;
                        };
                        const phase =
                            typeof j.phase === "string" ? j.phase : undefined;
                        if (phase && phase !== "idle") {
                            sawBusyPhase = true;
                        } else if (phase === "idle" && sawBusyPhase) {
                            resolveIdleAfterBusy?.();
                        }
                        setMapStorageProgress((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      phase: phase ?? prev.phase,
                                      progress:
                                          typeof j.progress === "number"
                                              ? j.progress
                                              : prev.progress,
                                      message:
                                          typeof j.message === "string"
                                              ? j.message
                                              : prev.message,
                                  }
                                : null,
                        );
                    } catch {
                        /* ignore */
                    }
                };
                await tick();
                pollId = window.setInterval(() => {
                    void tick();
                }, 150);
            }

            try {
                if (completeWhenIdle && pollHttp) {
                    // Prefer progress-idle completion: service reply may never
                    // arrive (Zenoh query timeout) even when the robot finished.
                    let opError: unknown;
                    const opSettled = op().then(
                        () => "op" as const,
                        (err: unknown) => {
                            opError = err;
                            return "op_err" as const;
                        },
                    );
                    const winner = await Promise.race([
                        opSettled,
                        idleAfterBusy.then(() => "idle" as const),
                    ]);
                    if (winner === "idle") {
                        // Idle can mean success or failure; briefly wait for the
                        // real service result if Zenoh still delivers it.
                        const settled = await Promise.race([
                            opSettled,
                            new Promise<"timeout">((resolve) => {
                                window.setTimeout(() => resolve("timeout"), 2500);
                            }),
                        ]);
                        if (settled === "op_err" && opError) {
                            throw opError;
                        }
                    } else if (winner === "op_err" && opError) {
                        // Service failed/timed out while robot may still be
                        // clearing — wait for HTTP idle before giving up.
                        const finishedViaProgress = await Promise.race([
                            idleAfterBusy.then(() => true),
                            new Promise<false>((resolve) => {
                                window.setTimeout(() => resolve(false), 90_000);
                            }),
                        ]);
                        if (!finishedViaProgress) {
                            throw opError;
                        }
                    }
                } else {
                    await op();
                }
            } finally {
                if (pollId !== undefined) {
                    window.clearInterval(pollId);
                }
                setMapStorageProgress(null);
            }
        },
        [isSecure, arcId],
    );

    const clearLoadState = useCallback(() => {
        setLoadStatus("idle");
        setLoadError(null);
    }, []);

    const requestClearKeepoutFilter = useCallback(
        async (request: ClearKeepoutFilterRequest) => {
            const [sn, pl, opts] = clearKeepoutFilterService(request);
            const res = (await callService(sn, pl, opts)) as {
                success?: boolean;
                message?: string;
            } | null;
            if (res?.success === false) {
                throw new Error(res.message || "Failed to clear keep-out filter");
            }
        },
        [callService],
    );

    const runAuroraGetStcmAction = useCallback(
        async (uri: string, uri2d: string) => {
            if (!actionCaller) {
                throw new Error("ROS connection unavailable");
            }

            await new Promise<void>((resolve, reject) => {
                let settled = false;
                const settleOnce = (fn: () => void) => {
                    if (settled) return;
                    settled = true;
                    fn();
                };

                actionCaller
                    .sendGoal(
                        "/aurora/get_stcm",
                        { uri, uri_2d: uri2d },
                        {
                            onResult: (result: unknown) => {
                                const r = (result ?? {}) as {
                                    success?: boolean;
                                    message?: string;
                                };
                                if (r.success === false) {
                                    settleOnce(() =>
                                        reject(
                                            new Error(
                                                r.message || "Aurora get_stcm action failed"
                                            )
                                        )
                                    );
                                    return;
                                }
                                settleOnce(resolve);
                            },
                            onFeedback: (feedback: unknown) => {
                                const f = (feedback ?? {}) as {
                                    phase?: string;
                                    progress?: number;
                                    status_message?: string;
                                    message?: string;
                                    feedback?: {
                                        phase?: string;
                                        progress?: number;
                                        status_message?: string;
                                        message?: string;
                                    };
                                };
                                const phase = f.phase ?? f.feedback?.phase ?? "running";
                                const rawProgress = f.progress ?? f.feedback?.progress ?? 0;
                                const progress =
                                    typeof rawProgress === "number"
                                        ? Math.max(0, Math.min(1, rawProgress))
                                        : 0;
                                const statusMessage = (
                                    f.status_message ??
                                    f.feedback?.status_message ??
                                    f.message ??
                                    f.feedback?.message ??
                                    ""
                                ).trim();

                                setMapStorageProgress((prev) =>
                                    prev
                                        ? {
                                              ...prev,
                                              phase,
                                              progress,
                                              message: statusMessage,
                                          }
                                        : prev
                                );
                            },
                            onFailed: (error: unknown) => {
                                const msg =
                                    error instanceof Error ? error.message : String(error);
                                settleOnce(() =>
                                    reject(
                                        new Error(
                                            msg || "Aurora get_stcm action transport error"
                                        )
                                    )
                                );
                            },
                        }
                    )
                    .catch((error) => {
                        settleOnce(() =>
                            reject(error instanceof Error ? error : new Error(String(error)))
                        );
                    });
            });
        },
        [actionCaller]
    );

    const loadStcmFromRelativePath = useCallback(
        async (relativePath: string, options?: LoadStcmOptions) => {
            await runWithDockedGateAsync(async () => {
                if (connectionState !== ConnectionState.Connected) {
                    const msg = "Not connected to ARC";
                    setLoadStatus("error");
                    setLoadError(msg);
                    addToast({ type: "error", message: msg });
                    return;
                }

                if (!actionCaller) {
                    const msg = "ROS connection unavailable";
                    setLoadStatus("error");
                    setLoadError(msg);
                    addToast({ type: "error", message: msg });
                    return;
                }

                const loadedStcmRel = relativePath.replace(/\\/g, "/").trim();
                const vRel =
                    options?.variantBmpRelativePath?.replace(/\\/g, "/").trim() ||
                    undefined;
                const mapFileId = options?.mapFileId?.trim() || undefined;

                let mapId = options?.mapId?.trim() || "";
                if (!mapId) {
                    try {
                        mapId =
                            (await missionMapIdForStcmRelPath(
                                loadedStcmRel,
                                selectedARC?.host,
                                arcId,
                            )) ?? "";
                    } catch (e) {
                        const msg =
                            e instanceof Error
                                ? e.message
                                : "Failed to resolve map in mission storage";
                        setLoadStatus("error");
                        setLoadError(msg);
                        addToast({ type: "error", message: msg });
                        return;
                    }
                }

                if (!mapId) {
                    const msg =
                        "Map is not registered in mission storage. Save or sync the map before loading.";
                    setLoadStatus("error");
                    setLoadError(msg);
                    addToast({ type: "error", message: msg });
                    return;
                }

                setLoadStatus("loading");
                setLoadError(null);

                try {
                    await runWithMapProgress(
                        "Loading map",
                        selectedARC?.host,
                        async () => {
                            const handle = performLoadMapAction(
                                actionCaller,
                                {
                                    map_id: mapId,
                                    variant_bmp_rel_path: vRel,
                                    map_file_id: mapFileId,
                                },
                                (feedback) => {
                                    const stage = feedback.stage ?? "running";
                                    const rawProgress = feedback.progress ?? 0;
                                    const progress =
                                        typeof rawProgress === "number" &&
                                        rawProgress >= 0
                                            ? Math.max(0, Math.min(1, rawProgress))
                                            : 0;
                                    const statusMessage = (
                                        feedback.status_message ??
                                        feedback.message ??
                                        ""
                                    ).trim();
                                    setMapStorageProgress((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  phase: stage,
                                                  progress,
                                                  message: statusMessage,
                                              }
                                            : prev,
                                    );
                                },
                            );
                            activeLoadMapCancelRef.current = handle.cancel;

                            const result = await handle.promise;
                            activeLoadMapCancelRef.current = null;
                            setCancellingLoadMap(false);

                            if (result.cancelled) {
                                setLoadStatus("idle");
                                setLoadError(null);
                                addToast({
                                    type: "info",
                                    message: "Map load cancelled",
                                });
                                throw new MapLoadCancelledError();
                            }

                            if (!result.success) {
                                throw new Error(
                                    result.message ||
                                        result.error ||
                                        "Load map action failed",
                                );
                            }
                        },
                        { pollHttpProgress: false, cancellable: true },
                    );
                } catch (e) {
                    activeLoadMapCancelRef.current = null;
                    setCancellingLoadMap(false);
                    if (e instanceof MapLoadCancelledError) {
                        return;
                    }
                    const msg =
                        e instanceof Error ? e.message : "Failed to load map";
                    setLoadStatus("error");
                    setLoadError(msg);
                    addToast({ type: "error", message: msg });
                    return;
                }

                setLoadStatus("success");
                recordLoadedStcmPath(loadedStcmRel);
                addToast({ type: "success", message: "Map loaded" });
            }, t("docking.chargingBodyMapLoad"));
        },
        [
            addToast,
            recordLoadedStcmPath,
            connectionState,
            actionCaller,
            runWithDockedGateAsync,
            t,
            runWithMapProgress,
            selectedARC?.host,
            arcId,
        ],
    );

    const saveMap = useCallback(
        async (baseName: string, remarks = "") => {
            if (connectionState !== ConnectionState.Connected) {
                addToast({ type: "error", message: "Not connected to ARC" });
                return;
            }

            let norm: string;
            try {
                norm = validateAndNormalizeMapBaseName(baseName);
            } catch (e) {
                const msg = e instanceof Error ? e.message : "Invalid name";
                addToast({ type: "error", message: msg });
                return;
            }

            const relStcm = `${norm}.stcm`;
            const stcmUri = `file:///home/r2c2/map/${norm}.stcm`;
            const bmpUri = `file:///home/r2c2/map/${norm}.png`;

            try {
                setMapStorageProgress({
                    title: "Saving map",
                    phase: "create_goal",
                    progress: 0,
                    message: "Starting map save…",
                });
                await runAuroraGetStcmAction(stcmUri, bmpUri);
            } catch (e) {
                const msg =
                    e instanceof Error ? e.message : "Failed to save map";
                addToast({ type: "error", message: msg });
                throw e;
            } finally {
                setMapStorageProgress(null);
            }
            const apiBase = getStcmMapListBaseUrl(selectedARC?.host, isSecure, arcId);
            let filterYamlNote = "";
            if (apiBase) {
                const meta = mapMetadata as {
                    width?: number;
                    height?: number;
                    resolution?: number;
                    origin?: { x?: number; y?: number };
                    originYaw?: number;
                } | null;
                const yaml = buildMapFilterYamlContent({
                    resolution: meta?.resolution,
                    originX: meta?.origin?.x,
                    originY: meta?.origin?.y,
                    originYaw: meta?.originYaw,
                    width: meta?.width,
                    height: meta?.height,
                });
                try {
                    await postStcmMetadata(apiBase, relStcm, remarks ?? "", {
                        filterYamlContent: yaml,
                    });
                } catch (e) {
                    const msg =
                        e instanceof Error
                            ? e.message
                            : "Failed to write map metadata";
                    addToast({
                        type: "error",
                        message: `Map saved on robot but metadata/filter YAML failed: ${msg}`,
                    });
                    // Do not fail the overall save flow after Aurora save succeeded.
                    // Metadata sync can be retried later without blocking user workflow.
                }
                const hasMeta =
                    meta &&
                    typeof meta.resolution === "number" &&
                    meta.origin &&
                    typeof meta.origin.x === "number" &&
                    typeof meta.origin.y === "number";
                if (!hasMeta) {
                    filterYamlNote =
                        " Filter YAML used defaults (no /map_metadata yet).";
                }
                try {
                    await requestClearKeepoutFilter({
                        mode: "stcm",
                        stcm_rel_path: relStcm,
                    });
                } catch (e) {
                    const msg =
                        e instanceof Error ? e.message : "Could not clear keep-out filter";
                    addToast({
                        type: "warning",
                        message: `Map saved; prior keep-out filter may still be active: ${msg}`,
                    });
                }
            }
            recordLoadedStcmPath(relStcm);
            addToast({
                type: "success",
                message: `Map saved.${filterYamlNote}`,
            });
        },
        [
            addToast,
            recordLoadedStcmPath,
            requestClearKeepoutFilter,
            connectionState,
            isSecure,
            arcId,
            mapMetadata,
            runAuroraGetStcmAction,
            selectedARC?.host,
        ]
    );

    const clearMap = useCallback(() => {
        if (connectionState !== ConnectionState.Connected) {
            addToast({ type: "error", message: "Not connected to ARC" });
            return;
        }

        void (async () => {
            try {
                // Service-only clear (no action server): overlay + optional HTTP
                // poll while backend runs requireMapReset then reinit_aurora.
                await runWithMapProgress(
                    "Clearing map",
                    selectedARC?.host,
                    async () => {
                        const [clearSn, clearPl, clearOpts] = performClearMap();
                        const clearRes = (await callService(
                            clearSn,
                            clearPl,
                            clearOpts,
                        )) as { success?: boolean; message?: string } | null;
                        if (clearRes?.success === false) {
                            throw new Error(
                                clearRes.message || "Clear map failed",
                            );
                        }
                    },
                    {
                        showOverlay: true,
                        initialPhase: "clear",
                        initialMessage: "",
                        // Clear+reinit often exceeds Zenoh's ~10s service reply
                        // budget; HTTP progress → idle is the completion signal.
                        completeWhenProgressIdle: true,
                    },
                );
                recordLoadedStcmPath(null);

                addToast({
                    type: "success",
                    message: "Map cleared",
                });
            } catch (e) {
                const msg =
                    e instanceof Error ? e.message : "Failed to clear map";
                addToast({ type: "error", message: msg });
            }
        })();
    }, [
        addToast,
        recordLoadedStcmPath,
        connectionState,
        callService,
        runWithMapProgress,
        selectedARC?.host,
    ]);

    const value = useMemo(
        () => ({
            loadStcmFromRelativePath,
            loadStatus,
            loadError,
            clearLoadState,
            lastLoadedStcmRelPath,
            saveMap,
            clearMap,
        }),
        [
            loadStcmFromRelativePath,
            loadStatus,
            loadError,
            clearLoadState,
            lastLoadedStcmRelPath,
            saveMap,
            clearMap,
        ]
    );

    return (
        <>
            {dockedGateModal}
            <MapStorageProgressOverlay
                visible={mapStorageProgress !== null}
                title={mapStorageProgress?.title ?? ""}
                snapshot={
                    mapStorageProgress
                        ? {
                              phase: mapStorageProgress.phase,
                              progress: mapStorageProgress.progress,
                              message: mapStorageProgress.message,
                          }
                        : null
                }
                onCancel={
                    mapStorageProgress?.cancellable ? cancelLoadMap : undefined
                }
                cancelling={cancellingLoadMap}
            />
            <MapManagerContext.Provider value={value}>
                {children}
            </MapManagerContext.Provider>
        </>
    );
};

export default MapManagerProvider;
